import React, { useState } from "react";
import { Play, Dot } from "lucide-react";
import { services, servicesQuote } from "../mock";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import EditableText from "./EditableText";

const VIDEOS = [
  {
    id: "l6NN2Or-vTk",
    title: "Sharada Rama Rao Trust — Activities",
    isShort: true,
  },
  {
    id: "si9DTj1j46Y",
    title: "Sharada Rama Rao Trust — Moments",
    isShort: false,
  },
];

const VideoCard = ({ videoId, title, isShort, onClick }) => {
  // Prefer high-quality thumb; fallback to hqdefault if maxres missing
  const [src, setSrc] = useState(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
  return (
    <button
      onClick={onClick}
      aria-label={title}
      className="group relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/15 w-[140px] sm:w-[170px] md:w-[200px] aspect-[3/5] focus:outline-none focus:ring-2 focus:ring-[#c47750] bg-black"
    >
      <img
        src={src}
        alt={title}
        onError={() => setSrc(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`)}
        className={`w-full h-full ${isShort ? "object-cover" : "object-contain bg-black"}`}
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10 pointer-events-none" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center transition-transform group-hover:scale-110">
          <Play className="text-white ml-1" size={26} fill="white" />
        </div>
      </div>
    </button>
  );
};

const Services = () => {
  const [openIdx, setOpenIdx] = useState(null);

  const closeVideo = () => setOpenIdx(null);
  const active = openIdx !== null ? VIDEOS[openIdx] : null;

  return (
    <section id="services" className="relative w-full bg-[#d6d7c5] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        <div>
          <EditableText id="services.title" as="h2"
              className="block text-[#1a1a1a] font-semibold mb-8"
              style={{ fontFamily: "'Quicksand', system-ui, sans-serif", fontSize: "clamp(2.4rem, 5vw, 4rem)" }}>
            Services
          </EditableText>
          <ul className="space-y-3">
            {services.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-[#1a1a1a] text-base md:text-lg">
                <Dot className="shrink-0 mt-1" size={28} />
                <EditableText id={`services.item.${i}`} as="span">{s}</EditableText>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => window.open("https://www.youtube.com/@SharadaRamaRaoTrust", "_blank", "noopener,noreferrer")}
              className="bg-[#d18a5c] hover:bg-[#bc7748] text-[#1a1a1a] rounded-full px-6 py-5 underline decoration-1 underline-offset-4 font-medium shadow-md"
            >
              <EditableText id="services.cta" as="span">Know more about this</EditableText>
            </Button>
            <a
              href="/activities"
              target="_blank"
              rel="noopener"
              referrerPolicy="no-referrer-when-downgrade"
              className="inline-flex items-center gap-1.5 bg-[#5c7c2e] hover:bg-[#4a6425] text-white rounded-full px-5 py-3 text-sm font-semibold shadow-md transition-colors"
            >
              View all activities
              <span aria-hidden="true">{"\u2197"}</span>
            </a>
          </div>
        </div>

        <div className="flex flex-col items-end">
          <EditableText id="services.quote" as="p" multiline
             className="text-right text-[#1a1a1a] whitespace-pre-line leading-snug mb-6 block"
             style={{ fontFamily: "'Patrick Hand', 'Quicksand', cursive", fontSize: "clamp(1.1rem, 2vw, 1.6rem)" }}>
            {servicesQuote}
          </EditableText>
          <div className="flex gap-4 md:gap-6 self-end">
            {VIDEOS.map((v, i) => (
              <VideoCard
                key={v.id}
                videoId={v.id}
                title={v.title}
                isShort={v.isShort}
                onClick={() => setOpenIdx(i)}
              />
            ))}
          </div>
        </div>
      </div>

      <Dialog open={openIdx !== null} onOpenChange={(o) => !o && closeVideo()}>
        <DialogContent className={active?.isShort ? "max-w-[420px] p-0 bg-black border-black" : "max-w-3xl p-0 bg-black border-black"}>
          <DialogHeader className="px-4 pt-3 pb-2 bg-black">
            <DialogTitle className="text-white text-sm font-medium flex items-center justify-between gap-3">
              <span className="truncate">{active?.title}</span>
              {active && (
                <a
                  href={`https://youtu.be/${active.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#ff8b62] hover:text-white underline underline-offset-2 shrink-0"
                >
                  Open on YouTube
                </a>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className={active?.isShort ? "w-full aspect-[9/16] bg-black" : "w-full aspect-video bg-black"}>
            {active && (
              <iframe
                key={active.id}
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${active.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                title={active.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Services;
